import { useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

// Global state to track if polling should start
let shouldStartPolling = false;

export const startCVPolling = () => {
  console.log('[CV Polling] Starting global polling...');
  shouldStartPolling = true;
};

export const useCVPolling = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const hasShownToast = useRef(false);
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Check every 500ms if we should start polling
    const checkInterval = setInterval(() => {
      if (shouldStartPolling && !pollingInterval.current) {
        console.log('[CV Polling] Starting polling interval...');
        shouldStartPolling = false; // Reset the flag
        hasShownToast.current = false; // Reset toast flag
        
        let pollCount = 0;
        const maxPolls = 30; // Poll for max 60 seconds (30 * 2 seconds)
        
        pollingInterval.current = setInterval(async () => {
          pollCount++;
          console.log(`[CV Polling] Poll #${pollCount}/${maxPolls} - checking CV status...`);
          
          try {
            const response = await fetch('/api/candidates/cv-status');
            if (!response.ok) {
              throw new Error('Failed to check status');
            }
            
            const data = await response.json();
            console.log('[CV Polling] Status response:', data);
            
            // Check if parsing is complete
            if (data.cvParsed === true && !hasShownToast.current) {
              console.log('[CV Polling] CV parsing complete! Showing toast...');
              
              if (pollingInterval.current) {
                clearInterval(pollingInterval.current);
                pollingInterval.current = null;
              }
              
              hasShownToast.current = true;
              
              // Show completion toast
              toast({
                title: "✨ Profile Fully Updated!",
                description: "Your CV has been completely analyzed. All your skills, experience, and achievements are now in your profile.",
                duration: 6000,
              });
              
              // Refresh profile data - use exact: false to match queries with additional key segments
              queryClient.invalidateQueries({ queryKey: ['candidate-profile'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['/api/candidates/profile'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['/api/candidates/cv-status'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['/api/auth/user'], exact: false });
            }
          } catch (error) {
            console.error('[CV Polling] Error checking CV status:', error);
          }
          
          // Stop polling after max attempts
          if (pollCount >= maxPolls) {
            console.log('[CV Polling] Max polls reached, stopping...');
            if (pollingInterval.current) {
              clearInterval(pollingInterval.current);
              pollingInterval.current = null;
            }
          }
        }, 2000); // Check every 2 seconds
      }
    }, 500);

    return () => {
      clearInterval(checkInterval);
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, [toast, queryClient]);

  return { isPolling: !!pollingInterval.current };
};